'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import UiIcon from '@/components/ui/UiIcon';

const PULL_THRESHOLD = 60;

export function attachProjectPullRefresh(element: HTMLElement, progress: (distance: number) => void,
  refresh: () => void, busy: () => boolean) {
  const phone = window.matchMedia('(max-width: 760px)');
  let tracking = false, startX = 0, startY = 0, distance = 0;
  const cancel = () => { tracking = false; distance = 0; progress(0); };
  const start = (event: TouchEvent) => {
    cancel();
    if (!phone.matches || busy() || window.scrollY > 1 || event.touches.length !== 1
      || (event.target as Element).closest('nav, input, select, textarea, [contenteditable], [aria-label="Категории проектов"]')) return;
    tracking = true;
    startX = event.touches[0].clientX; startY = event.touches[0].clientY;
  };
  const move = (event: TouchEvent) => {
    if (!tracking) return;
    if (event.touches.length !== 1 || window.scrollY > 1 || !event.cancelable) { cancel(); return; }
    const dy = event.touches[0].clientY - startY, dx = event.touches[0].clientX - startX;
    if (dy <= 0 || Math.abs(dx) > dy) { cancel(); return; }
    // Только явный вертикальный жест у верхней границы заменяет нативное обновление браузера.
    event.preventDefault();
    distance = Math.min(84, dy * .45);
    progress(distance);
  };
  const end = () => {
    const shouldRefresh = tracking && distance >= PULL_THRESHOLD && !busy();
    cancel();
    if (shouldRefresh) refresh();
  };
  element.addEventListener('touchstart', start, { passive: true });
  element.addEventListener('touchmove', move, { passive: false });
  element.addEventListener('touchend', end);
  element.addEventListener('touchcancel', cancel);
  phone.addEventListener('change', cancel);
  document.addEventListener('visibilitychange', cancel);
  return () => {
    element.removeEventListener('touchstart', start);
    element.removeEventListener('touchmove', move);
    element.removeEventListener('touchend', end);
    element.removeEventListener('touchcancel', cancel);
    phone.removeEventListener('change', cancel);
    document.removeEventListener('visibilitychange', cancel);
  };
}

export default function ProjectPullRefresh({ children, refreshing, onRefresh }: {
  children: ReactNode; refreshing: boolean; onRefresh: () => Promise<void>;
}) {
  const root = useRef<HTMLDivElement>(null);
  const latest = useRef({ refreshing, onRefresh });
  latest.current = { refreshing, onRefresh };
  const pending = useRef(false);
  const mounted = useRef(true);
  const [distance, setDistance] = useState(0);
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState(false);
  const refresh = useCallback(() => {
    if (pending.current || latest.current.refreshing) return;
    pending.current = true; setRunning(true); setFailed(false);
    void Promise.resolve().then(() => latest.current.onRefresh()).catch(() => {
      if (mounted.current) setFailed(true);
    }).finally(() => {
      pending.current = false;
      if (mounted.current) setRunning(false);
    });
  }, []);
  useEffect(() => {
    mounted.current = true;
    const dispose = attachProjectPullRefresh(root.current!, setDistance, refresh, () => pending.current || latest.current.refreshing);
    return () => { mounted.current = false; dispose(); };
  }, [refresh]);
  return <div ref={root} className="min-h-screen text-site-text relative overflow-x-clip">
    {children}
    <button type="button" onClick={refresh} disabled={refreshing || running}
      className="sr-only focus:not-sr-only min-[761px]:hidden">Обновить проекты</button>
    {(distance > 0 || running || failed) && <div role="status" className="fixed left-1/2 top-[calc(var(--lisad-header-h,88px)+8px)] z-40 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-site-surface px-4 py-2 text-xs text-site-text shadow-lg min-[761px]:hidden">
      <UiIcon name="ArrowsClockwise" className={running ? 'motion-safe:animate-spin' : undefined} />
      {running ? 'Обновляем проекты…' : failed ? 'Не удалось обновить проекты' : distance >= PULL_THRESHOLD ? 'Отпустите для обновления' : 'Потяните вниз для обновления'}
    </div>}
  </div>;
}
